// apps/app/src/routes/Courses/Lesson/state.ts

import { api } from '@api'
import { userId } from '@state/auth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/solid-query'
import { createEffect, createMemo, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { useCourseOverview } from '../useCourseOverview'

const fetchLessonDetails = async (lessonSlug?: string) => {
  if (!lessonSlug) return null

  const { data, error } = await api()
    .from('course_lessons')
    .select(`
      *,
      section:course_sections (
        id,
        title,
        course:courses (
          id,
          title
        )
      ),
      course_progress (
        status,
        seconds_watched
      )
    `)
    .eq('slug', lessonSlug)
    .single()

  if (error) throw new Error(error.message)
  return data
}

export function useLessonState() {
  const queryClient = useQueryClient()
  const [lessonSlug, setLessonSlug] = createSignal<string>()
  const [requirementsMet, setRequirementsMet] = createSignal(false)
  const { activeLessonOverviewQuery, activeCourseslug } =
    useService(useCourseOverview)()

  createEffect(() => {
    lessonSlug()
    setRequirementsMet(false)
  })

  // Single Query: Fetches Lesson + Current User's Progress
  const lessonQuery = useQuery(() => ({
    queryKey: ['lesson', lessonSlug()],
    queryFn: () => fetchLessonDetails(lessonSlug()),
    enabled: !!lessonSlug(),
    staleTime: 1000 * 60 * 5,
  }))

  // Helper to extract the single progress object from the array Supabase returns
  const currentProgress = createMemo(() => {
    const progressArray = lessonQuery.data?.course_progress
    return progressArray && progressArray.length > 0 ? progressArray[0] : null
  })

  const updateProgressMutation = useMutation(() => ({
    mutationFn: async (payload: {
      lessonId: string
      status: 'in_progress' | 'completed'
    }) => {
      const user_id = userId()!

      const { error } = await api().from('course_progress').upsert(
        {
          user_id,
          lesson_id: payload.lessonId,
          status: payload.status,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,lesson_id' },
      )

      if (error) throw new Error(error.message)
      return payload.status
    },
    onSuccess: (data, variables) => {
      // Invalidate the main lesson query so it refetches the updated course_progress array
      queryClient.invalidateQueries({ queryKey: ['lesson', lessonSlug()] })
      queryClient.invalidateQueries({
        queryKey: ['course_overview', activeCourseslug()],
      })
      if (!nextLessonSlug() && variables.status === 'completed') {
				queryClient.invalidateQueries({ queryKey: ['courses'] })
			}
    },
  }))

  // Effect: Auto-set "in_progress" if the array is empty (no record exists)
  createEffect(() => {
    const lesson = lessonQuery.data
    const isLoaded = lessonQuery.isSuccess

    if (isLoaded && lesson && !currentProgress()) {
      updateProgressMutation.mutate({
        lessonId: lesson.id,
        status: 'in_progress',
      })
    }
  })

  const markAsComplete = () => {
    const lesson = lessonQuery.data
    if (lesson?.id) {
      updateProgressMutation.mutate({
        lessonId: lesson.id,
        status: 'completed',
      })
    }
  }

  const nextLessonSlug = createMemo(() => {
    const courseData = activeLessonOverviewQuery.data
    const currentSlug = lessonSlug()

    if (!courseData?.course_sections || !currentSlug) return null

    const orderedLessons = [...courseData.course_sections]
      .sort((a, b) => a.index - b.index)
      .flatMap((section) => {
        if (!section.course_lessons) return []
        return [...section.course_lessons].sort((a, b) => a.index - b.index)
      })

    const currentIndex = orderedLessons.findIndex((l) => l.slug === currentSlug)

    if (currentIndex !== -1 && currentIndex < orderedLessons.length - 1) {
      return orderedLessons[currentIndex + 1].slug
    }
    return null
  })

  return {
    lessonSlug,
    setLessonSlug,
    lessonQuery,
    requirementsMet,
    setRequirementsMet,
    currentProgress,
    nextLessonSlug,
    markAsComplete,
    isUpdatingProgress: updateProgressMutation.isPending,
  }
}
